/**
 * A folha de impressão do mês da escala, em paisagem, que o navegador salva como PDF.
 *
 * Papel: desenha a folha — o monograma e o logotipo Villa Prado com a linha
 * "Calendário da residência — MÊS ANO" à direita, a linha dos dias da semana, a
 * grade do mês com a paleta própria do papel e a legenda — e, assim que monta,
 * abre a janela de impressão do navegador com ela.
 *
 * Como usar: montar este componente é o que imprime, e por isso quem o monta só
 * o faz no clique de "🖨 Imprimir / PDF", com uma `key` nova a cada clique, e o
 * desmonta quando `onConcluida` é chamado, depois que a janela de impressão
 * fecha — a impressão é de `useImpressaoAoMontar`, que imprime uma vez por
 * montagem. Deixá-lo montado faria o Ctrl+P seguinte, dado na tela, sair com a
 * folha no lugar da tela. Enquanto ele está montado, quem o monta também deverá
 * esconder da impressão todo o resto da página (`print:hidden`), senão o papel
 * sai com a tela e a folha depois dela.
 *
 * Na tela a folha nunca aparece (`hidden`): ela só existe para a mídia de
 * impressão (`print:flex`). A janela de impressão só abre depois que as duas
 * imagens da marca terminam de carregar — aberta antes, a folha sairia sem a
 * marca no topo.
 *
 * A folha pede a página nomeada `folha-paisagem`, de `index.css`, e tem a
 * altura da área útil dela (`h-[194mm]`); a grade ocupa o que sobra do
 * cabeçalho e da legenda, com as semanas em alturas iguais, e o que não cabe
 * numa célula é cortado por ela, sem empurrar a semana de baixo. O `<main>` da
 * moldura tem 32 px de respiro nas laterais, e a folha os desfaz com
 * `print:-mx-8` e `print:w-[calc(100%+4rem)]`; se o respiro do `<main>` mudar,
 * as duas compensações terão de mudar junto.
 *
 * O tom de cada célula é o da primeira situação do dia na ordem família, todos,
 * folga geral e feriado — no papel, o dia de família com todos na casa sai com
 * o tom de família, e os dois selos dizem o resto. As células dos meses
 * vizinhos saem em cinza, com o que está gravado nelas: a escala se monta por
 * semana, e a semana da virada sai inteira no papel.
 */
import {
  type Ausencia,
  type DiaCalendario,
  type Visita,
  cobreODia,
  feriadoDoDia,
} from '@concierge/shared';

import { useImpressaoAoMontar } from '@/shared/hooks';
import { cn } from '@/shared/lib/utils';
import { comoChamar } from '../escala';
import {
  MESES,
  type MesMostrado,
  type PrimeiroDia,
  celulasDoMes,
  diasDaSemanaNaGrade,
} from '../grade';

interface FolhaDoCalendarioProps {
  mostrado: MesMostrado;
  /** O dia em que cada semana começa, o mesmo da grade da tela. */
  primeiroDia: PrimeiroDia;
  dias: DiaCalendario[];
  ausencias: Ausencia[];
  visitas: Visita[];
  /** Chamado quando a janela de impressão fecha, impressa ou cancelada. */
  onConcluida: () => void;
}

/** O fundo e a borda de cada situação no papel, na ordem de prioridade. */
const TOM_NO_PAPEL = [
  ['familia', 'border-folha-familia-borda bg-folha-familia'],
  ['todos_residencia', 'border-folha-todos-borda bg-folha-todos'],
  ['folga_total', 'border-folha-folga-borda bg-folha-folga'],
  ['feriado', 'border-folha-feriado-borda bg-folha-feriado'],
] as const;

/** O selo de cada situação no papel: o texto, que o CSS põe em caixa alta, e o fundo. */
const SELO_NO_PAPEL = {
  familia: { rotulo: 'Família', fundo: 'bg-folha-familia-selo' },
  todos_residencia: { rotulo: 'Todos', fundo: 'bg-folha-todos-selo' },
  folga_total: { rotulo: 'Folga Todos', fundo: 'bg-brass-light' },
  feriado: { rotulo: 'Feriado', fundo: 'bg-folha-feriado-selo' },
} as const;

/** As amostras de tom da legenda da folha. */
const AMOSTRAS_NO_PAPEL = [
  { tom: 'border-folha-familia-borda bg-folha-familia', significado: 'Família na residência' },
  { tom: 'border-folha-todos-borda bg-folha-todos', significado: 'Todos na residência' },
  { tom: 'border-folha-folga-borda bg-folha-folga', significado: 'Folga TODOS' },
  { tom: 'border-folha-feriado-borda bg-folha-feriado', significado: 'Feriado (manual)' },
] as const;

/** A etiqueta de uma pessoa no papel, em plantão ou de folga. */
const ETIQUETA = {
  plantao: 'bg-folha-etiqueta-plantao text-folha-familia-selo',
  folga: 'bg-folha-etiqueta-folga text-brass-light',
} as const;

/**
 * Uma linha de pessoas da célula no papel.
 *
 * @param rotulo - `Res:` ou `Folga:`, que o CSS põe em caixa alta.
 * @param nomes - Os nomes curtos, na ordem em que aparecem.
 * @param papel - Qual das duas etiquetas desenhar.
 * @returns A linha, cortada na segunda linha de texto, ou nada quando vazia.
 */
function LinhaNoPapel({
  rotulo,
  nomes,
  papel,
}: {
  rotulo: string;
  nomes: string[];
  papel: keyof typeof ETIQUETA;
}) {
  if (nomes.length === 0) return null;

  return (
    <div className="flex max-h-[2.6em] flex-wrap items-center gap-0.5 overflow-hidden leading-[1.3]">
      <span className="mr-0.5 font-mono text-[6.8px] text-texto-fraco uppercase">
        {rotulo}
      </span>
      {nomes.map((nome, indice) => (
        <span
          key={`${nome}-${String(indice)}`}
          className={cn(
            'inline-block rounded-[2px] px-1 py-[0.5px] text-[7.8px]',
            ETIQUETA[papel],
          )}
        >
          {nome}
        </span>
      ))}
    </div>
  );
}

/**
 * A folha de impressão, que abre a janela de impressão ao montar.
 *
 * @param mostrado - O mês impresso, que dá a linha do topo e a grade.
 * @param primeiroDia - O dia em que cada semana começa.
 * @param dias - Os dias com registro da grade inteira.
 * @param ausencias - As ausências que encostam na grade.
 * @param visitas - As visitas que encostam na grade.
 * @param onConcluida - Chamado uma vez, quando o navegador avisa que a janela
 *   de impressão fechou (`afterprint`).
 * @returns A folha, escondida na tela e visível só na impressão.
 */
export function FolhaDoCalendario({
  mostrado,
  primeiroDia,
  dias,
  ausencias,
  visitas,
  onConcluida,
}: FolhaDoCalendarioProps) {
  const refDaFolha = useImpressaoAoMontar<HTMLDivElement>(onConcluida);

  const porDia = new Map(dias.map((dia) => [dia.dia, dia]));
  const mesPorExtenso = `${MESES[mostrado.mes - 1] ?? ''} ${String(mostrado.ano)}`;

  return (
    <div
      ref={refDaFolha}
      className="hidden h-[194mm] flex-col bg-superficie p-[8mm_10mm] font-sans text-foreground [page:folha-paisagem] [print-color-adjust:exact] print:-mx-8 print:flex print:w-[calc(100%+4rem)]"
    >
      <div className="mb-2.5 flex shrink-0 items-center justify-between gap-3.5 border-b border-linha-forte pb-2">
        <div className="flex items-center gap-2.5">
          <img
            src="/marca/villa-prado-monograma.png"
            alt="Villa Prado"
            width={26}
            height={26}
            className="block size-[26px] shrink-0 object-contain"
          />
          <span aria-hidden="true" className="h-[18px] w-px shrink-0 bg-linha-forte" />
          <img
            src="/marca/villa-prado-logotipo.png"
            alt="Villa Prado"
            className="block h-[26px] w-auto object-contain"
          />
        </div>
        <p className="font-mono text-[9.5px] tracking-[0.06em] text-texto-medio uppercase">
          Calendário da residência — {mesPorExtenso}
        </p>
      </div>

      <div className="mb-[3px] grid shrink-0 grid-cols-7 gap-[3px]">
        {diasDaSemanaNaGrade(primeiroDia).map((diaDaSemana) => (
          <div
            key={diaDaSemana.nome}
            className={cn(
              'px-1 py-1.5 text-center font-mono text-[10px] tracking-[0.06em] uppercase',
              diaDaSemana.fimDeSemana ? 'text-brass' : 'text-texto-fraco',
            )}
          >
            {diaDaSemana.nome}
          </div>
        ))}
      </div>

      <div className="grid min-h-0 flex-auto auto-rows-fr grid-cols-7 gap-[3px]">
        {celulasDoMes(mostrado, primeiroDia).map((celula) => {
          const dia = porDia.get(celula.dia);
          const status = dia?.status ?? [];
          const escalados = dia?.escalados ?? [];
          const feriado = status.includes('feriado') ? null : feriadoDoDia(celula.dia);
          const daVisita = visitas.filter((v) => cobreODia(v, celula.dia));
          const deFerias = ausencias.filter((a) => cobreODia(a, celula.dia));
          const tom = TOM_NO_PAPEL.find(([situacao]) => status.includes(situacao))?.[1];

          return (
            <div
              key={celula.dia}
              className={cn(
                'flex min-h-0 flex-col gap-px overflow-hidden rounded-[3px] border border-linha-forte bg-superficie px-[5px] py-[3px]',
                celula.doMes ? tom : 'bg-superficie-2 text-texto-fraco',
              )}
            >
              {status.length > 0 && (
                <div className="flex flex-wrap gap-0.5">
                  {TOM_NO_PAPEL.filter(([situacao]) => status.includes(situacao)).map(
                    ([situacao]) => (
                      <span
                        key={situacao}
                        className={cn(
                          'rounded-[2px] px-[5px] py-[1.5px] font-mono text-[6.8px] font-bold tracking-[0.02em] whitespace-nowrap text-superficie uppercase',
                          SELO_NO_PAPEL[situacao].fundo,
                        )}
                      >
                        {SELO_NO_PAPEL[situacao].rotulo}
                      </span>
                    ),
                  )}
                </div>
              )}
              {feriado !== null && (
                <p className="font-mono text-[7px] text-folha-feriado-selo">{feriado}</p>
              )}
              <LinhaNoPapel
                rotulo="Res:"
                nomes={escalados.filter((p) => p.papel === 'plantao').map(comoChamar)}
                papel="plantao"
              />
              <LinhaNoPapel
                rotulo="Folga:"
                nomes={escalados.filter((p) => p.papel === 'folga').map(comoChamar)}
                papel="folga"
              />
              {daVisita.length > 0 && (
                <p className="text-[7.8px] font-bold text-folha-extra">
                  +{' '}
                  {daVisita
                    .map((v) =>
                      v.observacao === null ? v.visitante : `${v.visitante} ${v.observacao}`,
                    )
                    .join(', ')}
                </p>
              )}
              {dia?.extras !== undefined && dia.extras !== null && (
                <p className="text-[7.8px] font-bold text-folha-extra">{dia.extras}</p>
              )}
              {dia?.observacao !== undefined && dia.observacao !== null && (
                <p className="text-[7.8px] text-texto-fraco italic">{dia.observacao}</p>
              )}
              {deFerias.length > 0 && (
                <p className="text-[7.8px] text-texto-fraco italic">
                  Férias: {deFerias.map((a) => comoChamar(a.colaborador)).join(', ')}
                </p>
              )}
              <p className="mt-auto shrink-0 self-start font-mono text-[9px] text-texto-medio">
                {celula.numero}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-1.5 flex shrink-0 flex-wrap gap-2.5 border-t border-folha-filete pt-[5px] font-mono text-[7.5px] text-texto-medio">
        {AMOSTRAS_NO_PAPEL.map((amostra) => (
          <div key={amostra.significado} className="flex items-center gap-1">
            <span
              aria-hidden="true"
              className={cn('inline-block size-[11px] rounded-[2px] border', amostra.tom)}
            />
            {amostra.significado}
          </div>
        ))}
        <div className="flex items-center gap-1">
          <span
            aria-hidden="true"
            className="inline-block size-[11px] rounded-[2px] border border-linha-forte text-center font-mono text-[7px] leading-[11px] font-bold text-folha-feriado-selo"
          >
            1
          </span>
          Feriado nacional
        </div>
        <div className="flex items-center gap-1">
          <span
            className={cn('rounded-[2px] px-[5px] py-[1.5px] text-[7.8px]', ETIQUETA.plantao)}
          >
            Res
          </span>
          Plantão
        </div>
        <div className="flex items-center gap-1">
          <span
            className={cn('rounded-[2px] px-[5px] py-[1.5px] text-[7.8px]', ETIQUETA.folga)}
          >
            Folga
          </span>
          Folga
        </div>
      </div>
    </div>
  );
}
